import Decimal from "decimal.js-light"
import { type FC, Fragment, type SetStateAction, useEffect, useState } from "react"
import { useParams } from "react-router"
import randomUUID from "uuid-random"

import { type CalculatedExamCompletion, type CourseExam } from "@forento/core/models/course"
import { clampNumber, parseIntNumber } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Checkbox, { Radio } from "~/components/Checkbox"
import Dropdown from "~/components/Dropdown"
import { CheckmarkIcon, CloseIcon, DeleteIcon } from "~/components/Icon"
import InputField from "~/components/InputField"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import { DefaultModal, ModalButtons, ModalInputs, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { ControlledTabs } from "~/components/Tabs"
import { useToast } from "~/contexts/ToastContext"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const ExamPage: FC = () => {
	const courseId = parseIntNumber(useParams().courseId) ?? -1

	const { data, error, refetch } = query.course.exam.getByCourse.useQuery(courseId, { staleTime: Infinity })

	return (
		<Layout>
			<PageBreadcrumb
				title="Exam"
				path={[
					{ title: "Courses", link: routes.course.index() },
					{ title: data?.course.title ?? "...", link: routes.course.detail(courseId) },
				]}
			/>
			{error ? (
				<p>Failed to load exam.</p>
			) : data === undefined ? (
				<PartialLoadingPage />
			) : (
				<ExamContent courseId={data.course.id} exam={data.exam} reload={refetch} />
			)}
		</Layout>
	)
}

type ExamContentProps = {
	courseId: number
	exam: Omit<CourseExam, "platformId" | "course"> | null
	reload(): Promise<unknown>
}
const ExamContent: FC<ExamContentProps> = ({ courseId, exam, reload }) => (
	<ControlledTabs
		tabs={[
			{
				title: "Answers",
				content: <Completions courseId={courseId} />,
			},
			{
				title: "Edit",
				content: <Edit courseId={courseId} exam={exam} reload={reload} />,
			},
		]}
	/>
)

const Completions: FC<{ courseId: number }> = ({ courseId }) => {
	const { data, error } = query.course.exam.listCompletionsByCourse.useQuery(courseId)

	return (
		<div className="mt-4">
			{error ? (
				<p>Failed to load answers.</p>
			) : data === undefined ? (
				<PartialLoadingPage />
			) : (
				<div className="flex flex-col gap-4">
					{data.length === 0 ? (
						<p>No answers have been submitted.</p>
					) : (
						data.map(c => <VersionCompletions key={c.exam.id} completions={c} />)
					)}
				</div>
			)}
		</div>
	)
}

type Completions = {
	exam: CourseExam
	completions: {
		user: { id: number; firstName: string; lastName: string }
		completion: CalculatedExamCompletion
	}[]
}
const VersionCompletions: FC<{ completions: Completions }> = ({ completions: { exam, completions } }) => (
	<div>
		<h2 className="mb-2 text-lg font-bold">Version {exam.version}</h2>
		<Table
			columns={["User", "Points", "Passed", "Actions"]}
			data={completions.map(c => [
				<div>
					<Button variant="link" onClick={routes.user.detail(c.user.id)}>
						{c.user.firstName} {c.user.lastName}
					</Button>
				</div>,
				`${c.completion.score.received} / ${c.completion.score.total} (${exam.passScore})`,
				c.completion.score.received >= exam.passScore ? (
					<CheckmarkIcon className="text-success h-4 w-4" />
				) : (
					<CloseIcon className="text-danger h-4 w-4" />
				),
				<ViewAnswers completion={c} exam={exam} />,
			])}
		/>
	</div>
)

type ViewAnswersProps = { completion: Completions["completions"][number]; exam: CourseExam }
const ViewAnswers: FC<ViewAnswersProps> = ({ completion, exam }) => {
	const [isModalOpen, setModalOpen] = useState(false)

	return (
		<>
			<DefaultModal onClose={() => setModalOpen(false)} isOpen={isModalOpen}>
				<ModalTitle>
					{completion.user.firstName} {completion.user.lastName}
				</ModalTitle>
				<ModalInputs className="flex flex-col items-center gap-4">
					{exam.questions.map(question =>
						question.options.filter(x => x.isCorrect).length === 1 ? (
							<Fragment key={question.id}>
								<h2 id={`exam-question-${question.id}`} className="text-center font-bold">
									{question.label}
								</h2>
								<fieldset
									aria-labelledby={`exam-question-${question.id}`}
									className="mb-6 flex flex-col gap-3"
								>
									{question.options.map(option => (
										<Radio
											key={option.id}
											name={`exam-question-${question.id}`}
											label={option.label}
											isChecked={completion.completion.answers.all.includes(option.id)}
											className={cn(
												option.isCorrect !==
													completion.completion.answers.all.includes(option.id) &&
													"text-danger line-through",
											)}
										/>
									))}
								</fieldset>
							</Fragment>
						) : (
							<Fragment key={question.id}>
								<h2 className="text-center font-bold">{question.label}</h2>
								<div className="mb-6 flex flex-col gap-3">
									{question.options.map(option => (
										<Checkbox
											key={option.id}
											label={option.label}
											isChecked={completion.completion.answers.all.includes(option.id)}
											className={cn(
												option.isCorrect !==
													completion.completion.answers.all.includes(option.id) &&
													"text-danger line-through",
											)}
										/>
									))}
								</div>
							</Fragment>
						),
					)}
				</ModalInputs>
				<ModalButtons>
					<Button variant="primary" onClick={() => setModalOpen(false)}>
						Close
					</Button>
				</ModalButtons>
			</DefaultModal>
			<Button variant="link" onClick={() => setModalOpen(true)}>
				View answers
			</Button>
		</>
	)
}

type EditProps = {
	courseId: number
	exam: Omit<CourseExam, "platformId" | "course"> | null
	reload(): Promise<unknown>
}
const Edit: FC<EditProps> = ({ courseId, exam, reload }) => {
	const toast = useToast()

	const [passScore, setPassScore] = useState(exam?.passScore ?? 0)
	const [questions, setQuestions] = useState(mapExamQuestions(exam))
	const [isModified, setModified] = useState(false)

	const totalPoints = questions.reduce(
		(acc, question) =>
			acc + (question.isPointsPerAnswer ? question.options.filter(x => x.isCorrect).length : 1) * question.points,
		0,
	)

	useEffect(() => {
		setPassScore(exam?.passScore ?? 0)
		setQuestions(mapExamQuestions(exam))
		setModified(false)
	}, [exam])

	useEffect(() => {
		if (isModified) {
			toast.setDiscardableUnsavedChanges({
				saveLabel: "Publish new version",
				async onSave() {
					const isAnyEmpty = questions.some(
						q => q.label.trim().length === 0 || q.options.some(o => o.label.trim().length === 0),
					)
					if (isAnyEmpty) {
						toast.showError("Question and options cannot be empty.")
						return false
					}
					const isNoneCorrect = questions.some(x => x.options.every(y => !y.isCorrect))
					if (isNoneCorrect) {
						toast.showError("At least one option must be correct on each question.")
						return false
					}
					if (passScore > totalPoints) {
						toast.showError("Passing score cannot be greater than the total points.")
						return false
					}
					try {
						await trpc.course.exam.create.mutate({ courseId, exam: { passScore, questions } })
						await reload()
						return true
					} catch (error) {
						console.error(error)
						toast.showError("Failed to create exam. Please try again later.")
						return false
					}
				},
				onDiscard() {
					setQuestions(mapExamQuestions(exam))
					setModified(false)
				},
			})
		} else {
			toast.clearUnsavedChanges()
		}
	}, [courseId, exam, isModified, passScore, questions, reload, toast, totalPoints])

	function handleSetQuestions(questions: SetStateAction<ExamQuestion[]>) {
		setQuestions(questions)
		setModified(true)
	}

	return (
		<div className="flex flex-col gap-4 pb-24">
			<div className="mt-4 flex items-end gap-4">
				<InputField
					label="Passing score"
					inputType="number"
					value={passScore.toString()}
					onChange={value => {
						const passScore = parseIntNumber(value)
						if (passScore === null) return
						setPassScore(clampNumber(passScore, 0, totalPoints))
						setModified(true)
					}}
					compact
					className="w-32"
				/>
				<p className="mb-[6px] text-sm text-gray-500">
					{totalPoints > 0 && `${new Decimal(passScore).div(totalPoints).mul(100).toDecimalPlaces(2)}% `}
					out of total {totalPoints} points
				</p>
			</div>
			{questions.map((question, index) => (
				<Question
					key={question.clientId}
					data={question}
					index={index}
					update={func =>
						handleSetQuestions(prev => prev.map(q => (q.clientId === question.clientId ? func(q) : q)))
					}
					remove={() => handleSetQuestions(prev => prev.filter(q => q.clientId !== question.clientId))}
				/>
			))}
			<Button
				variant="primary"
				onClick={() =>
					handleSetQuestions(prev => [
						...prev,
						{
							clientId: randomUUID(),
							label: "",
							points: 1,
							isPointsPerAnswer: false,
							options: [],
						},
					])
				}
				className="self-start"
			>
				Add question
			</Button>
		</div>
	)
}

function mapExamQuestions(exam: ExamContentProps["exam"]): ExamQuestion[] {
	if (exam === null) return []
	return exam.questions.map(x => ({
		clientId: randomUUID(),
		label: x.label,
		points: x.points,
		isPointsPerAnswer: x.isPointsPerAnswer,
		options: x.options.map(y => ({
			clientId: randomUUID(),
			label: y.label,
			isCorrect: y.isCorrect,
		})),
	}))
}

interface ExamQuestion {
	clientId: string
	label: string
	points: number
	isPointsPerAnswer: boolean
	options: { clientId: string; label: string; isCorrect: boolean }[]
}
type QuestionProps = {
	data: ExamQuestion
	index: number
	update(args: (current: ExamQuestion) => ExamQuestion): void
	remove(): void
}
const Question: FC<QuestionProps> = ({ data, index, update, remove }) => (
	<div key={data.clientId} className="rounded-lg border border-gray-200 p-4">
		<div className="mb-4 flex items-end justify-between gap-4">
			<InputField
				label={`Question ${index + 1}`}
				value={data.label}
				onChange={value => update(prev => ({ ...prev, label: value }))}
				compact
				className="grow"
			/>
			<InputField
				label="Points"
				value={data.points.toString()}
				onChange={value =>
					update(prev => ({ ...prev, points: clampNumber(parseIntNumber(value) ?? 0, 0, Infinity) }))
				}
				compact
				className="w-20"
			/>
			{data.options.filter(x => x.isCorrect).length > 1 && (
				<Dropdown
					items={[
						{ id: "total", title: "Total" },
						{ id: "answer", title: "Per answer" },
					]}
					onChange={value => update(prev => ({ ...prev, isPointsPerAnswer: value === "answer" }))}
					selectedItemId={data.isPointsPerAnswer ? "answer" : "total"}
					className="max-w-36"
				/>
			)}
			<button type="button" onClick={remove} className="text-danger mb-[6px] cursor-pointer">
				<DeleteIcon className="h-6 w-6" />
			</button>
		</div>
		<div className="mx-4 flex flex-col gap-2">
			{data.options.map(option => (
				<div key={option.clientId} className="flex items-center gap-4">
					<InputField
						value={option.label}
						onChange={value =>
							update(prev => ({
								...prev,
								options: prev.options.map(o =>
									o.clientId === option.clientId ? { ...o, label: value } : o,
								),
							}))
						}
						compact
						className="grow"
					/>
					<Checkbox
						ariaLabel="Correct answer"
						isChecked={option.isCorrect}
						onChange={value =>
							update(prev => ({
								...prev,
								options: prev.options.map(o =>
									o.clientId === option.clientId ? { ...o, isCorrect: value } : o,
								),
							}))
						}
					/>
					<button
						type="button"
						onClick={() =>
							update(prev => ({
								...prev,
								options: prev.options.filter(o => o.clientId !== option.clientId),
							}))
						}
						className="text-danger cursor-pointer"
					>
						<DeleteIcon className="h-5 w-5" />
					</button>
				</div>
			))}
			<Button
				variant="primary"
				onClick={() =>
					update(prev => ({
						...prev,
						options: [...prev.options, { clientId: randomUUID(), label: "", isCorrect: false }],
					}))
				}
				className="self-start"
			>
				Add option
			</Button>
		</div>
	</div>
)

export default ExamPage
